import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import uploadToDropbox from "../../../services/dropboxService";
import { ConfirmDialog } from "../../admin";

const VideosTab = ({ matches }) => {
  const [videos, setVideos] = useState([]);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [deletingVideoId, setDeletingVideoId] = useState(null);
  const [videoToDelete, setVideoToDelete] = useState(null);

  const [newVideo, setNewVideo] = useState({
    title: "",
    description: "",
    matchId: "",
    videoFile: null,
    thumbnailFile: null,
    videoUrl: "",
    thumbnailUrl: "",
    duration: "",
    tags: [],
  });

  const predefinedTags = [
    "Free football international and local videos",
    "Local amateur uploaded videos highlights",
    "Trending football videos",
  ];

  // Fetch existing videos on component mount
  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/videos`);
      if (!response.ok) throw new Error("Failed to fetch videos");
      const data = await response.json();
      setVideos(data);
    } catch (error) {
      toast.error("Failed to load videos");
      console.error(error);
    }
  };

  const handleVideoUpload = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate file size (max 500MB)
      if (newVideo.videoFile.size > 500 * 1024 * 1024) {
        toast.error("Video file size must be less than 500MB");
        return;
      }

      // Upload video to Dropbox
      const videoUrl = await uploadToDropbox(newVideo.videoFile, (progress) =>
        setUploadProgress(Math.round(progress * 100))
      );

      // Upload thumbnail if provided
      let thumbnailUrl = "";
      if (newVideo.thumbnailFile) {
        thumbnailUrl = await uploadToDropbox(
          newVideo.thumbnailFile,
          () => {} // Empty callback for thumbnail upload
        );
      }

      // Create video record in database
      const videoData = {
        title: newVideo.title,
        description: newVideo.description,
        matchId: parseInt(newVideo.matchId),
        videoUrl,
        thumbnailUrl,
        duration: newVideo.duration,
        tags: newVideo.tags,
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/videos`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(videoData),
      });

      if (!response.ok) throw new Error("Failed to save video data");

      const savedVideo = await response.json();

      // Set the preview video
      setPreviewVideo(savedVideo);

      // Reset form but don't close the modal yet
      setNewVideo({
        title: "",
        description: "",
        matchId: "",
        videoFile: null,
        thumbnailUrl: "",
        videoUrl: "",
        thumbnailFile: null,
        duration: "",
        tags: [],
      });

      fetchVideos();
      toast.success("Video uploaded successfully!");
    } catch (error) {
      toast.error("Failed to upload video");
      console.error(error);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const handleClosePreview = () => {
    setPreviewVideo(null);
    setShowUploadForm(false);
  };

  const handleDeleteVideo = async () => {
    if (!videoToDelete) return;
    const videoId = videoToDelete.id;
    setDeletingVideoId(videoId);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/videos/${videoId}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) throw new Error("Failed to delete video");

      setVideos((currentVideos) => currentVideos.filter((video) => video.id !== videoId));
      setVideoToDelete(null);
      toast.success("Video deleted successfully!");
    } catch (error) {
      toast.error("Failed to delete video");
      console.error(error);
    } finally {
      setDeletingVideoId(null);
    }
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between mb-6">
        <h2 className="text-2xl font-bold text-white">Match Videos</h2>
        <button
          onClick={() => setShowUploadForm(true)}
          className="bg-accent hover:bg-accent-hover text-white px-4 py-2 rounded-lg"
        >
          Upload New Video
        </button>
      </div>

      {/* Videos Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {videos.map((video) => {
          const associatedMatch = matches.find(
            (match) => match.matchesId === video.matchId
          );
          return (
            <div
              key={video.id}
              className="bg-surface-800 rounded-lg overflow-hidden"
            >
              <div className="aspect-w-16 aspect-h-9">
                {video.thumbnailUrl ? (
                  <img
                    src={video.thumbnailUrl}
                    alt={video.title}
                    className="object-cover w-full h-full"
                  />
                ) : (
                  <div className="bg-surface-700 w-full h-full flex items-center justify-center">
                    <span className="text-gray-400">No thumbnail</span>
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-semibold text-white">
                    {video.title}
                  </h3>
                  <button
                    onClick={() => setVideoToDelete(video)}
                    disabled={deletingVideoId === video.id}
                    className="text-accent-soft hover:text-accent transition-colors"
                  >
                    {deletingVideoId === video.id ? (
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {associatedMatch && (
                  <div className="mb-2 p-2 bg-surface-700 rounded-lg">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-2">
                        <img
                          src={associatedMatch.homeTeam?.logo}
                          alt={associatedMatch.homeTeam?.name}
                          className="w-6 h-6 object-contain"
                        />
                        <span className="text-white">
                          {associatedMatch.homeTeam?.name}
                        </span>
                      </div>
                      <span className="text-gray-300">vs</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-white">
                          {associatedMatch.awayTeam?.name}
                        </span>
                        <img
                          src={associatedMatch.awayTeam?.logo}
                          alt={associatedMatch.awayTeam?.name}
                          className="w-6 h-6 object-contain"
                        />
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 mt-1 text-center">
                      {new Date(associatedMatch.date).toLocaleDateString()}
                    </div>
                  </div>
                )}
                <p className="text-gray-400 text-sm mb-2">
                  {video.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {video.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-surface-700 text-gray-300 px-2 py-1 rounded-full text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {videoToDelete && (
        <ConfirmDialog
          title="Delete video"
          message={`Delete “${videoToDelete.title}”? This action cannot be undone.`}
          confirmLabel="Delete video"
          onConfirm={handleDeleteVideo}
          onCancel={() => setVideoToDelete(null)}
          busy={deletingVideoId === videoToDelete.id}
        />
      )}

      {/* Upload Form Modal */}
      {showUploadForm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-surface-900 rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {previewVideo ? (
              <div>
                <h3 className="text-xl font-bold text-white mb-4">
                  Video Uploaded Successfully!
                </h3>
                <div className="mb-4">
                  <h4 className="text-lg font-semibold text-white mb-2">
                    Preview
                  </h4>
                  <div className="relative w-full aspect-video bg-surface-800 rounded-lg overflow-hidden">
                    <video
                      className="w-full h-full"
                      controls
                      poster={previewVideo.thumbnailUrl}
                    >
                      <source src={previewVideo.videoUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={handleClosePreview}
                    className="bg-accent hover:bg-accent-hover text-white px-4 py-2 rounded-lg"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-bold text-white mb-4">
                  Upload Match Video
                </h3>

                <form onSubmit={handleVideoUpload} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Video Title
                    </label>
                    <input
                      type="text"
                      value={newVideo.title}
                      onChange={(e) =>
                        setNewVideo({ ...newVideo, title: e.target.value })
                      }
                      className="w-full bg-surface-800 text-white rounded px-3 py-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Match
                    </label>
                    <select
                      value={newVideo.matchId}
                      onChange={(e) =>
                        setNewVideo({ ...newVideo, matchId: e.target.value })
                      }
                      className="w-full bg-surface-800 text-white rounded px-3 py-2"
                      required
                    >
                      <option value="">Select Match</option>
                      {matches.map((match) => (
                        <option key={match.matchesId} value={match.matchesId}>
                          {`${match.homeTeam?.name} vs ${
                            match.awayTeam?.name
                          } - ${new Date(match.date).toLocaleDateString()}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Video File (Max 500MB)
                    </label>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={(e) =>
                        setNewVideo({
                          ...newVideo,
                          videoFile: e.target.files[0],
                        })
                      }
                      className="w-full bg-surface-800 text-white rounded px-3 py-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Thumbnail Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        setNewVideo({
                          ...newVideo,
                          thumbnailFile: e.target.files[0],
                        })
                      }
                      className="w-full bg-surface-800 text-white rounded px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={newVideo.description}
                      onChange={(e) =>
                        setNewVideo({
                          ...newVideo,
                          description: e.target.value,
                        })
                      }
                      className="w-full bg-surface-800 text-white rounded px-3 py-2"
                      rows="3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Video Category
                    </label>
                    <select
                      value={newVideo.tags[0] || ""}
                      onChange={(e) => {
                        setNewVideo({
                          ...newVideo,
                          tags: e.target.value ? [e.target.value] : [],
                        });
                      }}
                      className="w-full bg-surface-800 text-white rounded px-3 py-2"
                    >
                      <option value="">Select a category</option>
                      {predefinedTags.map((tag) => (
                        <option key={tag} value={tag}>
                          {tag}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Duration (minutes)
                    </label>
                    <input
                      type="number"
                      value={newVideo.duration}
                      onChange={(e) =>
                        setNewVideo({ ...newVideo, duration: e.target.value })
                      }
                      className="w-full bg-surface-800 text-white rounded px-3 py-2"
                      min="1"
                      required
                    />
                  </div>

                  {loading && (
                    <div className="w-full bg-surface-700 rounded-full h-2.5">
                      <div
                        className="bg-accent h-2.5 rounded-full"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  )}

                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setShowUploadForm(false)}
                      className="bg-surface-700 hover:bg-surface-700 text-white px-4 py-2 rounded-lg"
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="bg-accent hover:bg-accent-hover text-white px-4 py-2 rounded-lg flex items-center"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <svg
                            className="animate-spin h-5 w-5 mr-2"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Uploading...
                        </>
                      ) : (
                        "Upload Video"
                      )}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideosTab;
